import React, { useEffect, useState } from "react";
import { Form, Input, Button, Card, Spin, Space, Row, Col, Select, Table, Checkbox, Tag } from "antd";
import { Plus, Trash2, Save, Sparkles } from "lucide-react";
import ApiSelect from "../../components/ApiSelect";
import UploadImage from "../../components/UploadImage";
import {
  useGetCmsConfig,
  useSaveCmsConfig,
} from "../../../hooks/api/cms/hooks";
import { useCategoryList } from "../../../hooks/api/category/hooks";
import { useProductList } from "../../../hooks/api/product/hooks";
import { useReviewList } from "../../../hooks/api/review/hooks";
import { useArtistList } from "../../../hooks/api/artist/hooks";

import { useMessageApi } from "../../../provider/AntdNotification";

const HomePageCMS: React.FC = () => {
  const [form] = Form.useForm();
  const messageApi = useMessageApi();
  const { data: configData, isLoading } = useGetCmsConfig<any>("HOMEPAGE");
  const saveMutation = useSaveCmsConfig<any>({
    page: "HOMEPAGE",
    onSuccess: () => {
      messageApi.success("Homepage configuration saved successfully.");
    },
    onError: (err: any) => messageApi.error(err?.message),
  });

  // Option fetching queries — limit set to 20, never passing isAll
  const { data: categoryData, refetchWithQuery: refetchCategory } =
    useCategoryList({ limit: 20, page: 1 });
  const { data: productData, refetchWithQuery: refetchProduct } =
    useProductList({ limit: 100, page: 1 });
  const { data: reviewData, refetchWithQuery: refetchReview } = useReviewList({
    limit: 100,
    page: 1,
  });
  const { data: artistData, refetchWithQuery: refetchArtist } = useArtistList({
    limit: 100,
    page: 1,
  });

  useEffect(() => {
    if (configData?.data) {
      const cfg = configData.data;

      // Parse selected category items with images
      const rawCategoryItems = cfg.categoryItems || [];
      const categoryItems = rawCategoryItems.length
        ? rawCategoryItems.map((item: any) => ({
            categoryId:
              typeof item.categoryId === "object" && item.categoryId !== null
                ? item.categoryId._id
                : item.categoryId || item._id || item,
            image: item.image || "",
          }))
        : (cfg.categoryIds || []).map((cat: any) => ({
            categoryId:
              typeof cat === "object" && cat !== null ? cat._id : cat,
            image:
              (cfg.categoryImages &&
                cfg.categoryImages[
                  typeof cat === "object" && cat !== null ? cat._id : cat
                ]) ||
              "",
          }));

      const productIds = (cfg.productIds || []).map(
        (item: any) => item._id || item,
      );
      const reviewIds = (cfg.reviewIds || []).map(
        (item: any) => item._id || item,
      );
      const artistIds = (cfg.artistIds || []).map(
        (item: any) => item._id || item,
      );

      form.setFieldsValue({
        hero: cfg.hero || {
          title: "Sculptures that Transform Space",
          bannerImage: [],
        },
        categoryTitle: cfg.categoryTitle || "Popular Categories",
        categoryItems,
        productTitle: cfg.productTitle || "Newly Chosen Sculptures",
        productIds,
        whyChoose: cfg.whyChoose || {
          title: "Why Choose ArtPark",
          subtitle: "Excellence in sculpture & fine art curation",
          items: [],
        },
        reviewTitle: cfg.reviewTitle || "Customer Reviews",
        reviewIds,
        artistTitle: cfg.artistTitle || "Featured Artists",
        artistIds,
      });
    }
  }, [configData, form]);

  const onFinish = (values: any) => {
    const rawCategoryItems = values.categoryItems || [];
    const categoryItems = rawCategoryItems
      .map((item: any) => ({
        categoryId:
          typeof item?.categoryId === "object" && item?.categoryId !== null
            ? item.categoryId._id
            : item?.categoryId || "",
        image: item?.image || "",
      }))
      .filter((item: any) => item.categoryId);

    const categoryIds = categoryItems.map((item: any) => item.categoryId);
    const categoryImages: Record<string, string> = {};
    categoryItems.forEach((item: any) => {
      if (item.image) categoryImages[item.categoryId] = item.image;
    });

    const payload = {
      ...values,
      categoryIds,
      categoryItems,
      categoryImages,
    };

    saveMutation.mutate(payload);
  };

  const categoryOptions = React.useMemo(() => {
    const map = new Map();
    (categoryData?.data || []).forEach((cat: any) => {
      if (cat && cat._id)
        map.set(String(cat._id), { label: cat.name, value: cat._id });
    });
    return Array.from(map.values());
  }, [categoryData]);

  const productOptions = React.useMemo(() => {
    const map = new Map();
    (productData?.data || []).forEach((p: any) => {
      if (p && p._id) map.set(String(p._id), { label: p.title, value: p._id });
    });
    return Array.from(map.values());
  }, [productData]);

  const reviewOptions = React.useMemo(() => {
    const map = new Map();
    (reviewData?.data || []).forEach((r: any) => {
      if (r && r._id)
        map.set(String(r._id), {
          label: `${r.name} - "${r.comment?.slice(0, 30)}..."`,
          value: r._id,
        });
    });
    return Array.from(map.values());
  }, [reviewData]);

  const artistOptions = React.useMemo(() => {
    const map = new Map();
    (artistData?.data || []).forEach((a: any) => {
      if (a && a._id) map.set(String(a._id), { label: a.name, value: a._id });
    });
    return Array.from(map.values());
  }, [artistData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-12">
      <div className="w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
              <Sparkles className="text-primary" size={24} /> Home Page Content
              Management
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Customize sections, featured categories, sculptures, reviews and
              artists displayed on the home page.
            </p>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="flex flex-col gap-6"
        >
          {/* HERO BANNER SECTION */}
          <Card
            title={
              <span className="font-semibold text-slate-800">
                Hero Section Banner
              </span>
            }
          >
            <Form.Item
              name={["hero", "title"]}
              label="Hero Title"
              rules={[{ required: true, message: "Please enter hero title" }]}
            >
              <Input placeholder="e.g. Sculptures that Transform Space" />
            </Form.Item>
            <Form.Item name={["hero", "bannerImage"]} label="Hero Banner Image">
              <Form.Item name={["hero", "bannerImage"]} noStyle>
                <UploadImage count={4} listType="picture-card" />
              </Form.Item>
            </Form.Item>
          </Card>

          {/* POPULAR CATEGORIES */}
          <Card
            title={
              <span className="font-semibold text-slate-800">
                Category Section
              </span>
            }
          >
            <Form.Item name="categoryTitle" label="Section Title">
              <Input placeholder="e.g. Popular Categories" />
            </Form.Item>

            <Form.List name="categoryItems">
              {(fields, { add, remove }) => (
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-700">
                      Category Items (Category & Image)
                    </span>
                    <Button
                      type="dashed"
                      size="small"
                      onClick={() => add({ categoryId: null, image: "" })}
                      icon={<Plus size={14} />}
                      className="flex items-center gap-1"
                    >
                      Add Category Item
                    </Button>
                  </div>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      className="flex items-center gap-4 p-4 bg-slate-50 border rounded-lg"
                    >
                      <div className="flex-1">
                        <Form.Item
                          {...restField}
                          name={[name, "categoryId"]}
                          label="Select Category"
                          className="!mb-0"
                          rules={[
                            {
                              required: true,
                              message: "Please select a category",
                            },
                          ]}
                        >
                          <ApiSelect
                            placeholder="Search & Select Category..."
                            options={categoryOptions}
                            refetchWithQuery={refetchCategory}
                            baseQuery={{ limit: 20, page: 1 }}
                            className="w-full"
                          />
                        </Form.Item>
                      </div>

                      <Form.Item
                        {...restField}
                        name={[name, "image"]}
                        label="Category Image"
                        className="!mb-0"
                      >
                        <Form.Item name={[name, "image"]} noStyle>
                          <UploadImage count={1} listType="picture-card" />
                        </Form.Item>
                      </Form.Item>

                      <Button
                        type="text"
                        danger
                        icon={<Trash2 size={16} />}
                        onClick={() => remove(name)}
                        className="mt-6"
                      />
                    </div>
                  ))}
                </div>
              )}
            </Form.List>
          </Card>

          {/* NEWLY CHOSEN SCULPTURES */}
          <Card
            title={
              <span className="font-semibold text-slate-800">
                Sculptures Section
              </span>
            }
          >
            <Form.Item name="productTitle" label="Section Title">
              <Input placeholder="e.g. Newly Chosen Sculptures" />
            </Form.Item>
            <Form.Item name="productIds" label="Selected Sculptures (Max 8)">
              <ApiSelect
                mode="multiple"
                maxCount={8}
                placeholder="Select Sculptures..."
                options={productOptions}
                refetchWithQuery={refetchProduct}
                baseQuery={{ limit: 100, page: 1 }}
                className="w-full"
              />
            </Form.Item>
          </Card>

          {/* WHY CHOOSE ARTPARK */}
          <Card
            title={
              <span className="font-semibold text-slate-800">
                Why Choose Section
              </span>
            }
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name={["whyChoose", "title"]} label="Section Title">
                  <Input placeholder="e.g. Why Choose ArtPark" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={["whyChoose", "subtitle"]}
                  label="Section Subtitle"
                >
                  <Input placeholder="Enter section subtitle..." />
                </Form.Item>
              </Col>
            </Row>

            <Form.List name={["whyChoose", "items"]}>
              {(fields, { add, remove }) => (
                <div className="flex flex-col gap-4 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-slate-700">
                      Feature Bullet Items
                    </span>
                    <Button
                      type="dashed"
                      size="small"
                      onClick={() => add()}
                      icon={<Plus size={14} />}
                      className="flex items-center gap-1"
                    >
                      Add Feature
                    </Button>
                  </div>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      className="flex items-start gap-4 p-4 bg-slate-50 border rounded-lg"
                    >
                      <div className="flex-1 grid grid-cols-2 gap-4">
                        <Form.Item
                          {...restField}
                          name={[name, "title"]}
                          label="Feature Title"
                          className="!mb-0"
                        >
                          <Input placeholder="e.g. Experienced Craftsmanship" />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, "description"]}
                          label="Description"
                          className="!mb-0"
                        >
                          <Input.TextArea
                            rows={1}
                            placeholder="Feature description..."
                          />
                        </Form.Item>
                      </div>
                      <Button
                        type="text"
                        danger
                        icon={<Trash2 size={16} />}
                        onClick={() => remove(name)}
                        className="mt-6"
                      />
                    </div>
                  ))}
                </div>
              )}
            </Form.List>
          </Card>

          {/* CUSTOMER REVIEWS */}
          <Card
            title={
              <span className="font-semibold text-slate-800">
                Customer Reviews Section
              </span>
            }
          >
            <Form.Item name="reviewTitle" label="Section Title">
              <Input placeholder="e.g. Customer Reviews" />
            </Form.Item>
            <p className="text-xs text-slate-500 font-normal">
              Reviews are automatically fetched from the active Customer Reviews list.
            </p>
          </Card>

          {/* FEATURED ARTISTS */}
          <Card
            title={
              <span className="font-semibold text-slate-800">
                Featured Artists Section
              </span>
            }
          >
            <Form.Item name="artistTitle" label="Section Title">
              <Input placeholder="e.g. Featured Artists" />
            </Form.Item>
            <Form.Item name="artistIds" label="Selected Artists (Max 8)">
              <ApiSelect
                mode="multiple"
                maxCount={8}
                placeholder="Select Featured Artists..."
                options={artistOptions}
                refetchWithQuery={refetchArtist}
                baseQuery={{ limit: 100, page: 1 }}
                className="w-full"
              />
            </Form.Item>
          </Card>
        </Form>
      </div>

      {/* Always Visible Sticky Footer Save Bar */}
      <div className="sticky bottom-4 bg-white/95 backdrop-blur border border-slate-200 py-3 px-6 rounded-xl z-40 flex items-center justify-end shadow-lg">
        <Button
          type="primary"
          size="large"
          icon={<Save size={18} />}
          loading={saveMutation.isPending}
          onClick={() => form.submit()}
          className="!bg-primary flex items-center gap-2 px-8 shadow-md"
        >
          Save Configuration
        </Button>
      </div>
    </div>
  );
};

export default HomePageCMS;
